import { TConnectionAction, TConnectionsState, ActionType } from "../../interfaces/store/connectionStore";
import { makeStore } from "../makeStore";

const filters = {
    searchValues: [{
        isValueSearchOr: 'yes',
        searchValue: '',
        searchByName: 'yes',
        searchByCompany: 'yes',
        searchByHeadline: 'yes',
        searchByLocation: 'yes',
        searchByJobTitle: 'yes',
        searchByNote: 'no',
        isOutterValueSearchOr: 'no',
    }],
    searchByFollowersValue: undefined,
    searchByFollowersMaxValue: undefined,
    hiddenConnectionsValue: { value: 'no', label: 'No' },
    contactInfo: { value: 'all', label: 'All' },
    liPhone: { value: 'all', label: 'All' },
    industry: { value: '', label: 'All Industries' },
    note: { value: 'all', label: 'All' },
    tagCondition: 'And',
    selectedTags: [],
    selectedExcludeTags: [],
    languageTab: 'And',
    languageCondition: 'And',
    selectedLanguages: [],
    connectedAtFrom: undefined,
    connectedAtTo: undefined,
    lastMessagedFrom: undefined,
    lastMessagedTo: undefined,
    conversationStatus: undefined,
    conversationOwners: undefined,
    lastMessagedAtArray: undefined,
    onlyLast: undefined,
    birthDateFrom: undefined,
    birthDateTo: undefined,
    usersMutualCondition: 'And',
    selectedUsersMutual: [],
    noTags: false,
    sort: '',
    sortClicked: false, // saves previous sort value in order to compare to the cuurent sort value
    savedFilter: undefined,
    privateEmail: { value: 'all', label: 'All' },
    businessEmail: { value: 'all', label: 'All' },
    phone: { value: 'all', label: 'All' },
    autotagsFilter: undefined,
    autotagsFilterOr: undefined,
    hasJobChanges: undefined,
    hasCompanyChanges: undefined,
    companyEmployeesMin: undefined,
    companyEmployeesMax: undefined,
    connectionIdsFilter: undefined,
    connectionIdsFilterMeta: undefined,
}

export const initialConnectionsFilter = {
    ...filters
}

/** Fresh campaign filter. Nested arrays are copied so they are not the Contacts filter's arrays. */
export const blankConnectionsFilter = () => ({
    ...initialConnectionsFilter,
    searchValues: initialConnectionsFilter.searchValues.map((item: any) => ({ ...item })),
    selectedTags: [],
    selectedExcludeTags: [],
    selectedLanguages: [],
    selectedUsersMutual: [],
})

export const selectConnectionsFilter = <T extends { connectionsFilter: any, campaignConnectionsFilter: any, connectionsFilterScope: 'contacts' | 'campaign' }>(store: T) =>
    store.connectionsFilterScope === 'campaign' ? store.campaignConnectionsFilter : store.connectionsFilter

// Read columnsOrder and columnsSetup from localStorage if available
let columnsOrderFromStorage = undefined;
let columnsSetupFromStorage = undefined;
let prospectsColumnsSetupFromStorage = undefined;
let prospectsColumnsOrderFromStorage = undefined;
try {
    const setup = localStorage.getItem('columns-setup');
    if (setup) {
        /* NOTE: The check for 'fullName' in JSON.parse(setup) exists due to past column name changes.
           Some users may have outdated localStorage configurations, so we reset the columns in that case.
           This specific 'if' condition can sometimes be removed if backward compatibility is no longer needed,
           and only the 'else' block (loading from storage) can remain. */
    
        if ('fullName' in JSON.parse(setup)) columnsSetupFromStorage = null;
        else {
            const parsedSetup = JSON.parse(setup);
            
            // add liPhone to columnsSetup if it doesn't exist
            if (!parsedSetup.hasOwnProperty('liPhone')) {
                parsedSetup.liPhone = true; // true = vidljiva kolona
                localStorage.setItem('columns-setup', JSON.stringify(parsedSetup));
                
                // add liPhone to columnsOrder if it doesn't exist
                const currentOrder = localStorage.getItem('columns-order');
                if (currentOrder) {
                    const parsedOrder = JSON.parse(currentOrder);
                    if (!parsedOrder.includes('liPhone')) {
                        // Find the index of contactInfo and insert liPhone after it
                        const contactInfoIndex = parsedOrder.indexOf('contactInfo');
                        if (contactInfoIndex !== -1) {
                            parsedOrder.splice(contactInfoIndex + 1, 0, 'liPhone');
                        } else {
                            // If contactInfo is not found, add liPhone at the end
                            parsedOrder.push('liPhone');
                        }
                        localStorage.setItem('columns-order', JSON.stringify(parsedOrder));
                    }
                }
            }

            if (!parsedSetup.hasOwnProperty('linkedinMutualProfiles')) {
                parsedSetup.linkedinMutualProfiles = true;
                localStorage.setItem('columns-setup', JSON.stringify(parsedSetup));

                const currentOrder = localStorage.getItem('columns-order');
                if (currentOrder) {
                    const parsedOrder = JSON.parse(currentOrder);
                    if (!parsedOrder.includes('linkedinMutualProfiles')) {
                        const mutualConnectionsIndex = parsedOrder.indexOf('mutualConnections');
                        if (mutualConnectionsIndex !== -1) {
                            parsedOrder.splice(mutualConnectionsIndex + 1, 0, 'linkedinMutualProfiles');
                        } else {
                            parsedOrder.push('linkedinMutualProfiles');
                        }
                        localStorage.setItem('columns-order', JSON.stringify(parsedOrder));
                    }
                }
            }

            // Add companySize to columnsSetup if it doesn't exist (default hidden)
            if (!parsedSetup.hasOwnProperty('companySize')) {
                parsedSetup.companySize = false; // false = hidden by default
                localStorage.setItem('columns-setup', JSON.stringify(parsedSetup));

                // add companySize to columnsOrder if it doesn't exist (place after company)
                const currentOrder = localStorage.getItem('columns-order');
                if (currentOrder) {
                    const parsedOrder = JSON.parse(currentOrder);
                    if (!parsedOrder.includes('companySize')) {
                        const companyIndex = parsedOrder.indexOf('company');
                        if (companyIndex !== -1) {
                            parsedOrder.splice(companyIndex + 1, 0, 'companySize');
                        } else {
                            parsedOrder.push('companySize');
                        }
                        localStorage.setItem('columns-order', JSON.stringify(parsedOrder));
                    }
                }
            }
            
            columnsSetupFromStorage = parsedSetup;
        }
    } 
    const order = localStorage.getItem('columns-order');
    if (order) columnsOrderFromStorage = JSON.parse(order);
    const prospectsOrder = localStorage.getItem('prospects-columns-order');
    if (prospectsOrder) prospectsColumnsOrderFromStorage = JSON.parse(prospectsOrder);
    const prospectsSetup = localStorage.getItem('prospects-columns-setup');
    if (prospectsSetup) {
        if ('fullName' in JSON.parse(prospectsSetup)) prospectsColumnsSetupFromStorage = null;
        else prospectsColumnsSetupFromStorage = JSON.parse(prospectsSetup);
    }   
} catch {}

const initialConnectionsParameters = {
    page: 1,
    pageSize: 50,
    total: 0,
    columnsSetup: columnsSetupFromStorage || {
        "userInfo": true,
        "lastMessaged": true,
        "followers": true,
        "headline": true,
        "jobTitle": true,
        "company": true,
        "companySize": false,
        "location": true,
        "dateConnected": true,
        "contactInfo": true,
        "liPhone": true,
        "tags": true,
        "languages": true,
        "dateOfBirth": true,
        "mutualConnections": true,
        "linkedinMutualProfiles": true,
        "enrichedPrivateEmails": true,
        "enrichedBusinessEmails": true,
        "enrichedPhones": true
    },
    columnsOrder: columnsOrderFromStorage || [
        "userInfo",
        "headline",
        "jobTitle",
        "company",
        "companySize",
        "location",
        "dateConnected",
        "contactInfo",
        "liPhone",
        "lastMessaged",
        "tags",
        "languages",
        "dateOfBirth",
        "followers",
        "enrichedPrivateEmails",
        "enrichedBusinessEmails",
        "enrichedPhones",
        "mutualConnections",
        "linkedinMutualProfiles"
    ],
    prospectsColumnsSetup: prospectsColumnsSetupFromStorage || {
        "userInfo": true,
        "addToLeadDelta": true,
        "mutualConnections": true,
        "headline": true,
        "jobTitle": true,
        "company": true,    
        "location": true,
        "languages": true,
        "followers": true,
        "linkedIn": true,
    },
    prospectsColumnsOrder: prospectsColumnsOrderFromStorage || [
        "userInfo",
        "addToLeadDelta",
        "mutualConnections",
        "headline",
        "jobTitle",
        "company",
        "location", 
        "languages",
        "followers",
        "linkedIn",
    ]
} 

const initialConnectionsFilterOptions = {
    tagFilterOptions: [],
    languageFilterOptions: [],
    autotagsFilterOptions: []
}

const initialScheduledRemovalParameters = {
    total: 0, // num of selected connections to be removed
    count: 0, // current number of removed
    inProgress: false // for stopping scheduled removal
}

const initialState: TConnectionsState = {
    connections: [],
    connectionsFilter: initialConnectionsFilter,
    campaignConnectionsFilter: blankConnectionsFilter(),
    connectionsFilterScope: 'contacts',
    connectionsParameters: initialConnectionsParameters,
    connectionFilterOptions: initialConnectionsFilterOptions,
    scheduledRemovalParameters: initialScheduledRemovalParameters,
    selectedConnectionsIds: [],
    customFieldDefinitions: [],
    enrichingConnections: [],
}

const reducer = (state: TConnectionsState, action: TConnectionAction): TConnectionsState => {
    switch (action.type) {
        case ActionType.SET_CONNECTIONS: return { ...state, connections: action.payload }
        case ActionType.PATCH_CONNECTION: return {
            ...state,
            connections: state.connections.map((c) =>
                c?._id === action.payload.id ? { ...c, ...action.payload.patch } : c
            )
        }
        case ActionType.SET_CONNECTIONS_FILTER: return { ...state, connectionsFilter: {...state.connectionsFilter, ...action.payload} }
        case ActionType.SET_CAMPAIGN_CONNECTIONS_FILTER: return { ...state, campaignConnectionsFilter: {...state.campaignConnectionsFilter, ...action.payload} }
        case ActionType.REPLACE_CAMPAIGN_CONNECTIONS_FILTER: return { ...state, campaignConnectionsFilter: action.payload }
        case ActionType.SET_CONNECTIONS_FILTER_SCOPE: return { ...state, connectionsFilterScope: action.payload }
        case ActionType.SET_CONNECTIONS_PARAMETERS: return { ...state, connectionsParameters: {...state.connectionsParameters, ...action.payload} }
        case ActionType.SET_CONNECTIONS_FILTER_OPTIONS: return { ...state, connectionFilterOptions: {...state.connectionFilterOptions, ...action.payload} }
        case ActionType.RESET_CONNECTIONS_PAGINATION: return {  ...state, connectionsParameters: {...initialConnectionsParameters }, connectionsFilter: {...initialConnectionsFilter} }
        case ActionType.SET_SCHEDULED_REMOVAL_PARAMETERS: return { ...state, scheduledRemovalParameters: {...state.scheduledRemovalParameters, ...action.payload} }
        case ActionType.SET_SELECTED_CONNECTIONS_IDS: return { ...state, selectedConnectionsIds: action.payload }
        case ActionType.SET_CUSTOM_FIELD_DEFINITIONS: return { ...state, customFieldDefinitions: action.payload }
        case ActionType.SET_ENRICHING_CONNECTIONS: return { ...state, enrichingConnections: action.payload }
        case ActionType.RESET_STORE: return { ...initialState, campaignConnectionsFilter: blankConnectionsFilter(), connectionsFilterScope: 'contacts' }
        default: return state
    }
}

const { StoreProvider: ConnectionsStoreProvider, useStoreContext: useConnectionsStore } = makeStore(reducer, initialState)

export { ConnectionsStoreProvider, useConnectionsStore }